/**
 * Performance Table Component for Customer Performance
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { CustomerProfile, CustomerRatingThreshold } from '../../lib/api';
import { getGradeInfo, getRatingStyle, getOTDColor, formatCurrency, isNARating } from './utils';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';

interface PerformanceTableProps {
  customers: CustomerProfile[];
  ratingThresholds: CustomerRatingThreshold[];
  currency: string;
  loading?: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasMultiplePages: boolean;
    goToPage: (page: number) => void;
  };
  onOpenGRNModal: (customerId: number, customerName: string, filterType?: string, value?: number) => void;
  onOpenNCRModal: (customerId: number, customerName: string, filterType?: string, value?: number) => void;
  onOpenPOLineModal: (
    customerId: number,
    customerName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean }
  ) => void;
}

export function PerformanceTable({
  customers,
  ratingThresholds,
  currency,
  loading,
  pagination,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableProps) {
  const qualityLabel = useQualityMeasureLabel();
  const getLetterGrade = (score: number | undefined) =>
    getGradeInfo(score, ratingThresholds).label;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Performance Overview ({pagination.totalCount})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : customers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No suppliers found</p>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pt-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel="customers"
                />
              </div>
            )}

            <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full min-w-[2400px]">
                {/* Multi-level Header */}
                {/* CR-4313: Sticky headers so column labels stay visible when scrolling */}
                <thead className="sticky top-0 z-20">
                  {/* Top-level header groups */}
                  <tr className="border-b bg-muted/50">
                    <th
                      className="p-3 text-left font-semibold sticky left-0 bg-muted/50 z-10"
                      style={{ minWidth: '200px' }}
                      rowSpan={2}
                    >
                      Supplier
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10"
                      style={{ minWidth: '120px', left: '200px' }}
                      rowSpan={2}
                    >
                      Supplier Code
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10"
                      style={{ minWidth: '200px', left: '320px' }}
                      rowSpan={2}
                    >
                      Customer
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10 border-r"
                      style={{ minWidth: '100px', left: '520px' }}
                      rowSpan={2}
                    >
                      Rating
                    </th>
                    <th className="p-3 text-center font-semibold" style={{ minWidth: '120px' }} rowSpan={2}>
                      Warehouse
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '700px' }}
                      colSpan={7}
                    >
                      Quality
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '500px' }}
                      colSpan={5}
                    >
                      Cost
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '500px' }}
                      colSpan={5}
                    >
                      Delivery (SO Line)
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '400px' }}
                      colSpan={4}
                    >
                      Delivery (GRN)
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '150px' }}
                      colSpan={1}
                    >
                      CO2 Emissions
                    </th>
                  </tr>
                  {/* Second-level headers */}
                  <tr className="border-b bg-muted/30">
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '100px' }}>
                      Qty Received
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Qty Rejected
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
                      Supplier Liability {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
                      Customer Liability {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Total {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      No. of NCRs
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Response Score
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '120px' }}>
                      Total Value
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Reject Value
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Customer Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Supplier Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Total Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
                      SO Lines
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      OTIF
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Arrears
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      OTIF Score
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Qty Diff
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
                      No. of GRNs
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Ontime
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Late
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Early
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '150px' }}>
                      Total CO2 Emissions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b hover:bg-muted/30 transition-colors">
                      {/* Customer Name - Frozen */}
                      <td className="p-3 sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2">
                          {customer.is_preferred && (
                            <span className="text-yellow-500" title="Strategic Supplier">
                              ★
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-sm">{customer.company_name}</div>
                          </div>
                        </div>
                      </td>

                      {/* Customer Code - Frozen */}
                      <td className="p-3 text-center sticky bg-background z-10" style={{ left: '200px' }}>
                        <span className="text-xs">{customer.customer_code || '-'}</span>
                      </td>

                      {/* Supplier Sites - Frozen */}
                      <td className="p-3 sticky bg-background z-10" style={{ left: '320px' }}>
                        <span className="text-xs">{customer.suppliers || '-'}</span>
                      </td>

                      {/* Rating - Frozen */}
                      <td className="p-3 text-center sticky bg-background z-10 border-r" style={{ left: '520px' }}>
                        {isNARating(customer) ? (
                          <Badge variant="secondary" className="font-bold">
                            N/A
                          </Badge>
                        ) : (
                          <Badge
                            className="font-bold"
                            style={getRatingStyle(customer.combined_score ?? undefined, ratingThresholds)}
                          >
                            {getLetterGrade(customer.combined_score ?? undefined)}
                          </Badge>
                        )}
                      </td>

                      {/* Warehouse */}
                      <td className="p-3 text-center">
                        <span className="text-xs">{customer.warehouse_name || '-'}</span>
                      </td>

                      {/* Quality Metrics */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              customer.company,
                              customer.company_name,
                              'Qty Received',
                              customer.qty_received
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {customer.qty_received || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(
                              customer.company,
                              customer.company_name,
                              'Qty Rejected',
                              customer.reject_qty
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {customer.reject_qty || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.supplier_liability_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.customer_liability_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm font-medium">{customer.total_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(customer.company, customer.company_name, 'NCRs', customer.ncr_count)
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {customer.ncr_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.response_score || 0}</span>
                      </td>

                      {/* Cost Metrics */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              customer.company,
                              customer.company_name,
                              'Total Value',
                              parseFloat(customer.total_value || '0')
                            )
                          }
                          className="text-sm font-medium text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {formatCurrency(customer.total_value || null, customer.currency || currency)}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(
                              customer.company,
                              customer.company_name,
                              'Reject Value',
                              parseFloat(customer.reject_value || '0')
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {formatCurrency(customer.reject_value || null, customer.currency || currency)}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.supplier_cost_of_quality || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.customer_cost_of_quality || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{customer.total_cost_of_quality || 0}</span>
                      </td>

                      {/* Delivery Metrics (PO Line) */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'SO Lines',
                              customer.po_line_count
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View SO Line details"
                        >
                          {customer.po_line_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'OTIF',
                              customer.otif_count
                            )
                          }
                          className="text-sm text-green-600 hover:underline cursor-pointer"
                          title="View OTIF details"
                        >
                          {customer.otif_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'Arrears',
                              customer.arrears_count
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View Arrears details"
                        >
                          {customer.arrears_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={`text-sm font-semibold ${getOTDColor(customer.otif_score_percentage || 0)}`}
                        >
                          {customer.otif_score_percentage?.toFixed(1) || 0}%
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'Qty Diff',
                              customer.qty_diff
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View Qty Diff details"
                        >
                          {customer.qty_diff || 0}
                        </button>
                      </td>

                      {/* Delivery Metrics (GRN) */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              customer.company,
                              customer.company_name,
                              'No. of GRNs',
                              customer.grn_count
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {customer.grn_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'Ontime Deliveries',
                              customer.ontime_count,
                              { status: 'received', on_hold: false }
                            )
                          }
                          className="text-sm text-green-600 hover:underline cursor-pointer"
                          title="View Ontime deliveries"
                        >
                          {customer.ontime_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'Late Deliveries',
                              customer.late_count,
                              { status: 'received', on_hold: true }
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View Late deliveries"
                        >
                          {customer.late_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              customer.company,
                              customer.company_name,
                              'Early Deliveries',
                              customer.early_count,
                              { status: 'received' }
                            )
                          }
                          className="text-sm text-blue-600 hover:underline cursor-pointer"
                          title="View Early deliveries"
                        >
                          {customer.early_count || 0}
                        </button>
                      </td>

                      {/* CO2 Emissions */}
                      <td className="p-3 text-center border-l">
                        <span className="text-sm">{customer.carbon_emission || 0}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pb-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel="customers"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
